import { useCallback, useEffect, type MutableRefObject } from "react";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type {
  DraftPair,
  DraftPlayer,
  PairMode,
} from "../../challenges/types/challengeDraft";
import {
  draftSideToChoice,
  normalizeCpf,
} from "../../challenges/utils/challengePairs";
import {
  createManualEventRegistration,
  type AdminEventRegistration,
} from "../services/eventRegistrationAdminService";
import { isFixedPairOnlyChallengeType } from "../utils/adminChallengeEditor";

const AUTO_REGISTRATION_SOFT_TIMEOUT_MS = 8000;
const AUTO_REGISTRATION_ERROR =
  "Não foi possível criar a inscrição automática para os participantes selecionados.";

type UseAdminChallengeAutoProvisionInput = {
  activeRegistrationCategoryKeyForModal: string;
  autoProvisionInFlightRef: MutableRefObject<Set<string>>;
  canAddRegistrationsForChallenge: boolean;
  challengeDetail: ChallengeDetail | null;
  challengeRegistrationByCpf: Record<string, AdminEventRegistration>;
  challengeRegistrationByPairId: Record<string, AdminEventRegistration>;
  currentPairMode: PairMode;
  draftPairs: DraftPair[];
  draftPlayers: DraftPlayer[];
  isTournamentChallenge: boolean;
  loadChallengeEventRegistrations: (eventId?: string | null) => Promise<void>;
  loadEventRegistrations: () => Promise<void>;
  manualRegistrationEventIdForChallenge: string;
  showChallengeModal: boolean;
  showErrorToast: (message: string) => void;
  token?: string | null;
};

export function useAdminChallengeAutoProvision({
  activeRegistrationCategoryKeyForModal,
  autoProvisionInFlightRef,
  canAddRegistrationsForChallenge,
  challengeDetail,
  challengeRegistrationByCpf,
  challengeRegistrationByPairId,
  currentPairMode,
  draftPairs,
  draftPlayers,
  isTournamentChallenge,
  loadChallengeEventRegistrations,
  loadEventRegistrations,
  manualRegistrationEventIdForChallenge,
  showChallengeModal,
  showErrorToast,
  token,
}: UseAdminChallengeAutoProvisionInput) {
  const autoProvisionMissingChallengeRegistrations = useCallback(async () => {
    if (!token) return;
    if (!showChallengeModal) return;
    if (!canAddRegistrationsForChallenge) return;
    if (!challengeDetail) return;
    if (challengeDetail.status === "finished") return;
    if (isTournamentChallenge) return;

    const eventId = String(manualRegistrationEventIdForChallenge || "").trim();
    if (!eventId) return;

    const shouldUseFixedPairs =
      currentPairMode === "fixed" ||
      isFixedPairOnlyChallengeType(challengeDetail.challengeType);
    const shouldPersistPreferredSide =
      String(challengeDetail.challengeType || "").trim().toLowerCase() ===
        "challenge" && !shouldUseFixedPairs;
    const autoProvisionScopeKey =
      String(challengeDetail.challengeType || "").trim().toLowerCase() ===
      "tournament"
        ? String(activeRegistrationCategoryKeyForModal || "").trim() ||
          "sem-categoria"
        : "evento";
    const inFlight = autoProvisionInFlightRef.current;
    const keysStarted: string[] = [];
    let startedCount = 0;

    const startBackgroundRegistration = (
      key: string,
      payload: Parameters<typeof createManualEventRegistration>[2],
    ) => {
      startedCount += 1;
      let finished = false;
      window.setTimeout(() => {
        if (!finished) {
          inFlight.delete(key);
        }
      }, AUTO_REGISTRATION_SOFT_TIMEOUT_MS);

      createManualEventRegistration(token, eventId, payload)
        .then(async (created) => {
          finished = true;
          const createdId = Number(created?.id || 0);
          if (Number.isFinite(createdId) && createdId > 0) {
            await loadEventRegistrations();
            await loadChallengeEventRegistrations(eventId);
          }
        })
        .catch((err) => {
          finished = true;
          const message =
            err instanceof Error && err.message
              ? err.message
              : AUTO_REGISTRATION_ERROR;
          showErrorToast(message);
        })
        .finally(() => {
          inFlight.delete(key);
        });
    };

    try {
      if (shouldUseFixedPairs) {
        const pendingPairs = draftPairs
          .filter((pair) => !challengeRegistrationByPairId[pair.id])
          .map((pair) => ({
            pairId: pair.id,
            cpf1: normalizeCpf(pair.cpf1),
            cpf2: normalizeCpf(pair.cpf2),
          }))
          .filter(
            (item) =>
              item.cpf1.length >= 8 &&
              item.cpf2.length >= 8 &&
              item.cpf1 !== item.cpf2,
          )
          .filter((item) => {
            const key = `pair:${autoProvisionScopeKey}:${item.pairId}`;
            if (inFlight.has(key)) return false;
            inFlight.add(key);
            keysStarted.push(key);
            return true;
          });

        for (const pair of pendingPairs) {
          startBackgroundRegistration(
            `pair:${autoProvisionScopeKey}:${pair.pairId}`,
            {
              cpf: pair.cpf1,
              participantMode: "dupla_fixa",
              teammateCpf: pair.cpf2,
              registrationCategoryKey: activeRegistrationCategoryKeyForModal,
              confirmPayment: false,
              adminNote: "manual-admin-auto",
              allowEligibilityOverride: true,
              overrideReason: "admin_pair_category_override",
            },
          );
        }
      } else {
        const pendingPlayers = draftPlayers
          .map((player) => ({
            cpf: normalizeCpf(player.cpf),
            preferredSide: draftSideToChoice(player.side) || "ambos",
          }))
          .filter((player) => player.cpf.length >= 8)
          .filter((player) => !challengeRegistrationByCpf[player.cpf])
          .filter((player) => {
            const key = `player:${autoProvisionScopeKey}:${player.cpf}`;
            if (inFlight.has(key)) return false;
            inFlight.add(key);
            keysStarted.push(key);
            return true;
          });

        for (const player of pendingPlayers) {
          startBackgroundRegistration(
            `player:${autoProvisionScopeKey}:${player.cpf}`,
            {
              cpf: player.cpf,
              participantMode: "individual",
              ...(shouldPersistPreferredSide
                ? { preferredSide: player.preferredSide }
                : {}),
              registrationCategoryKey: activeRegistrationCategoryKeyForModal,
              confirmPayment: false,
              adminNote: "manual-admin-auto",
            },
          );
        }
      }

      if (startedCount > 0) return;
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : AUTO_REGISTRATION_ERROR;
      showErrorToast(message);
    } finally {
      if (startedCount === 0) {
        keysStarted.forEach((key) => inFlight.delete(key));
      }
    }
  }, [
    activeRegistrationCategoryKeyForModal,
    autoProvisionInFlightRef,
    canAddRegistrationsForChallenge,
    challengeDetail,
    challengeRegistrationByCpf,
    challengeRegistrationByPairId,
    currentPairMode,
    draftPairs,
    draftPlayers,
    isTournamentChallenge,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    manualRegistrationEventIdForChallenge,
    showChallengeModal,
    showErrorToast,
    token,
  ]);

  useEffect(() => {
    void autoProvisionMissingChallengeRegistrations();
  }, [autoProvisionMissingChallengeRegistrations]);
}
